import React, { useId, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ExternalLink, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import LoadFailure from "../../components/ui/LoadFailure";
import { useT, useLocalized } from "../../lib/LanguageContext";
import { hrefProblem } from "../../lib/platformHref";
import { HQ_SITE } from "../../constants/testIds/hqSite";
import siteApi from "./siteApi";
import useSiteLinks from "./useSiteLinks";
import { siteErrorMessage } from "./siteErrors";

/**
 * Links tab: the marketing site's navigation (contract §3.3) — the header list and one
 * list per footer group, each in display order. The public Navbar and footer render
 * exactly these rows; nothing else.
 *
 * The href hint mirrors the server's validator (lib/platformHref.js) so an href the
 * server will refuse is flagged while it is typed; the server stays the authority.
 */

const emptyLink = (location, group) => ({
  location, group, label_fr: "", label_en: "", label_es: "", href: "", open_in_new_tab: false,
});

const sameBucket = (a, b) => a.location === b.location && (a.location === "header" || a.group === b.group);

function LinkForm({ initial, schema, onSubmit, onCancel, t }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const baseId = useId();
  const set = (patch) => { setForm((prev) => ({ ...prev, ...patch })); setError(""); };
  const hrefIssue = form.href.trim() === "" ? null : hrefProblem(form.href, {
    anchors: schema.anchors, maxLength: schema.limits.href,
  });
  const hrefHints = {
    tooLong: t("hsite.links.href.tooLong", { max: schema.limits.href }),
    unknownAnchor: t("hsite.links.href.unknownAnchor", { anchors: schema.anchors.map((a) => `#${a}`).join(", ") }),
    invalid: t("hsite.links.href.invalid"),
  };
  const locationLabels = { header: t("hsite.links.header"), footer: t("hsite.links.footer") };
  const groupLabels = {
    product: t("hsite.links.group.product"),
    company: t("hsite.links.group.company"),
    legal: t("hsite.links.group.legal"),
  };
  const canSubmit = form.label_en.trim() !== "" && form.href.trim() !== "" && !hrefIssue && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onSubmit({
        ...form,
        group: form.location === "header" ? null : form.group,
        href: form.href.trim(),
      });
    } catch (err) {
      setError(siteErrorMessage(err, t));
      setBusy(false);
    }
  };

  const labelField = (locale, required) => (
    <div key={locale}>
      <label htmlFor={`${baseId}-label-${locale}`} className="block text-xs text-slate-500 mb-1">
        {t("hsite.links.label", { locale: locale.toUpperCase() })}{required ? " *" : ""}
      </label>
      <Input
        id={`${baseId}-label-${locale}`}
        value={form[`label_${locale}`]}
        required={required}
        onChange={(e) => set({ [`label_${locale}`]: e.target.value })}
        className="bg-white border-slate-200"
        data-testid={HQ_SITE.linkLabel(locale)}
      />
    </div>
  );

  return (
    <form onSubmit={submit} className="grid md:grid-cols-3 gap-3 p-4 rounded-2xl bg-sol-gold/5 border border-sol-gold/20" data-testid={HQ_SITE.linkForm}>
      {labelField("en", true)}
      {labelField("fr", false)}
      {labelField("es", false)}
      <div className="md:col-span-2">
        <label htmlFor={`${baseId}-href`} className="block text-xs text-slate-500 mb-1">{t("hsite.links.hrefLabel")} *</label>
        <Input
          id={`${baseId}-href`}
          value={form.href}
          required
          onChange={(e) => set({ href: e.target.value })}
          placeholder={t("hsite.links.hrefPlaceholder")}
          aria-describedby={`${baseId}-href-hint`}
          aria-invalid={Boolean(hrefIssue) || undefined}
          className="bg-white border-slate-200 font-mono text-xs"
          data-testid={HQ_SITE.linkHref}
        />
        <p
          id={`${baseId}-href-hint`}
          className={`mt-1 text-xs ${hrefIssue ? "text-rose-600" : "text-slate-400"}`}
          data-testid={HQ_SITE.linkHrefHint}
        >
          {hrefIssue ? hrefHints[hrefIssue] : t("hsite.links.href.help")}
        </p>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-600 self-start mt-6">
        <input
          type="checkbox"
          checked={form.open_in_new_tab}
          onChange={(e) => set({ open_in_new_tab: e.target.checked })}
          data-testid={HQ_SITE.linkNewTab}
        />
        {t("hsite.links.newTab")}
      </label>
      <div>
        <label htmlFor={`${baseId}-location`} className="block text-xs text-slate-500 mb-1">{t("hsite.links.location")}</label>
        <select
          id={`${baseId}-location`}
          value={form.location}
          onChange={(e) => set({ location: e.target.value, group: e.target.value === "header" ? null : (form.group || schema.footer_groups[0]) })}
          className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm"
          data-testid={HQ_SITE.linkLocation}
        >
          {schema.link_locations.map((l) => <option key={l} value={l}>{locationLabels[l] || l}</option>)}
        </select>
      </div>
      {form.location === "footer" && (
        <div>
          <label htmlFor={`${baseId}-group`} className="block text-xs text-slate-500 mb-1">{t("hsite.links.groupLabel")}</label>
          <select
            id={`${baseId}-group`}
            value={form.group || ""}
            onChange={(e) => set({ group: e.target.value })}
            className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm"
            data-testid={HQ_SITE.linkGroup}
          >
            {schema.footer_groups.map((g) => <option key={g} value={g}>{groupLabels[g] || g}</option>)}
          </select>
        </div>
      )}
      {error && (
        <p role="alert" className="md:col-span-3 text-xs text-rose-600" data-testid={HQ_SITE.linkFormError}>{error}</p>
      )}
      <div className="md:col-span-3 flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-xl" data-testid={HQ_SITE.linkCancel}>
          {t("hsite.links.cancel")}
        </Button>
        <Button type="submit" disabled={!canSubmit} className="bg-sol-gold text-black hover:bg-sol-gold/90 rounded-xl" data-testid={HQ_SITE.linkSubmit}>
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {t("hsite.links.save")}
        </Button>
      </div>
    </form>
  );
}

function LinkRow({ link, index, count, links, onEdit, t, readOnly }) {
  const loc = useLocalized();
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const label = loc(link, "label");

  const run = async (work, success) => {
    setBusy(true);
    try {
      await work();
      if (success) toast.success(success);
    } catch (e) {
      toast.error(siteErrorMessage(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="flex items-center gap-3 text-sm p-3 rounded-lg bg-white border border-slate-100" data-testid={HQ_SITE.linkRow(link.link_id)}>
      {!readOnly && (
      <div className="flex flex-col">
        <button
          type="button"
          disabled={busy || index === 0}
          onClick={() => run(() => links.move(link, -1))}
          aria-label={t("hsite.links.moveUp", { label })}
          className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
          data-testid={HQ_SITE.linkUp(link.link_id)}
        >
          <ArrowUp className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          disabled={busy || index === count - 1}
          onClick={() => run(() => links.move(link, 1))}
          aria-label={t("hsite.links.moveDown", { label })}
          className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
          data-testid={HQ_SITE.linkDown(link.link_id)}
        >
          <ArrowDown className="w-3.5 h-3.5" />
        </button>
      </div>
      )}
      <span className="font-medium flex-1 min-w-0 truncate">{label}</span>
      <code className="text-xs text-amber-700 truncate max-w-[40%]">{link.href}</code>
      {link.open_in_new_tab && (
        <ExternalLink className="w-3.5 h-3.5 text-slate-400" role="img" aria-label={t("hsite.links.newTab")} />
      )}
      {!readOnly && (
      <>
      <button
        type="button"
        onClick={() => onEdit(link)}
        disabled={busy}
        aria-label={t("hsite.links.edit", { label })}
        className="text-slate-400 hover:text-slate-700"
        data-testid={HQ_SITE.linkEdit(link.link_id)}
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={confirmDelete
          ? () => run(() => links.remove(link.link_id), t("hsite.links.deleted"))
          : () => setConfirmDelete(true)}
        onBlur={() => setConfirmDelete(false)}
        disabled={busy}
        aria-label={confirmDelete ? t("hsite.links.confirmDelete", { label }) : t("hsite.links.delete", { label })}
        className={`inline-flex items-center gap-1 text-xs ${confirmDelete ? "text-rose-600 font-medium" : "text-rose-400"}`}
        data-testid={HQ_SITE.linkDelete(link.link_id)}
      >
        <Trash2 className="w-3.5 h-3.5" />
        {confirmDelete && t("hsite.links.confirmDeleteShort")}
      </button>
      </>
      )}
    </li>
  );
}

export default function LinksTab({ schema, api = siteApi, readOnly = false }) {
  const t = useT("superadmin");
  const links = useSiteLinks({ api });
  // null | { mode: "create", initial } | { mode: "edit", link, initial }
  const [editing, setEditing] = useState(null);

  if (links.status === "loading") return <Loader2 className="w-6 h-6 animate-spin text-amber-600" />;
  if (links.status === "error") {
    return (
      <LoadFailure
        message={siteErrorMessage(links.error, t)}
        onRetry={links.reload}
        retryLabel={t("hsite.retry")}
        testId={HQ_SITE.loadError}
        retryTestId={HQ_SITE.loadRetry}
      />
    );
  }

  const buckets = [
    { location: "header", group: null, title: t("hsite.links.header") },
    ...schema.footer_groups.map((group) => ({
      location: "footer",
      group,
      title: t("hsite.links.footerGroup", {
        group: { product: t("hsite.links.group.product"), company: t("hsite.links.group.company"), legal: t("hsite.links.group.legal") }[group] || group,
      }),
    })),
  ];

  const submit = async (body) => {
    if (editing.mode === "create") {
      await links.create(body);
      toast.success(t("hsite.links.created"));
    } else {
      // `order` is omitted: the server keeps the link's position, or appends it to its new
      // bucket when the edit moved it.
      await links.update(editing.link.link_id, body);
      toast.success(t("hsite.links.updated"));
    }
    setEditing(null);
  };

  const formFor = (bucket) => editing
    && sameBucket(editing.mode === "edit" ? editing.link : editing.initial, bucket);

  return (
    <div className="space-y-8">
      <p className="text-sm text-slate-500 max-w-3xl">{t("hsite.links.hint")}</p>
      {buckets.map((bucket) => {
        const rows = links.bucket(bucket.location, bucket.group);
        return (
          <section key={`${bucket.location}-${bucket.group}`} data-testid={HQ_SITE.linkBucket(bucket.location, bucket.group)}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-sans text-lg text-slate-800">{bucket.title}</h2>
              {!readOnly && <Button
                type="button"
                size="sm"
                onClick={() => setEditing({ mode: "create", initial: emptyLink(bucket.location, bucket.group) })}
                className="bg-sol-gold text-black hover:bg-sol-gold/90 rounded-xl h-8 text-xs"
                data-testid={HQ_SITE.linkAdd(bucket.location, bucket.group)}
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> {t("hsite.links.add")}
              </Button>}
            </div>
            {!readOnly && formFor(bucket) && (
              <div className="mb-3">
                <LinkForm
                  key={editing.mode === "edit" ? editing.link.link_id : "create"}
                  initial={editing.initial}
                  schema={schema}
                  onSubmit={submit}
                  onCancel={() => setEditing(null)}
                  t={t}
                />
              </div>
            )}
            {rows.length === 0 ? (
              <p className="text-slate-400 text-sm py-4">{t("hsite.links.empty")}</p>
            ) : (
              <ul className="space-y-1">
                {rows.map((link, index) => (
                  <LinkRow
                    key={link.link_id}
                    link={link}
                    index={index}
                    count={rows.length}
                    links={links}
                    t={t}
                    readOnly={readOnly}
                    onEdit={(l) => setEditing({
                      mode: "edit",
                      link: l,
                      initial: {
                        location: l.location,
                        group: l.location === "header" ? null : l.group,
                        label_fr: l.label_fr || "",
                        label_en: l.label_en || "",
                        label_es: l.label_es || "",
                        href: l.href || "",
                        open_in_new_tab: Boolean(l.open_in_new_tab),
                      },
                    })}
                  />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
